import type {
  EnvironmentProviders} from '@angular/core';
import {
  APP_INITIALIZER,
  makeEnvironmentProviders,
} from '@angular/core';
import { defer, map, of } from 'rxjs';
import {
  LogLevel,
  StsConfigHttpLoader,
  StsConfigLoader,
  provideAuth as provideOidcAuth,
} from 'angular-auth-oidc-client';

import { AppConfigService } from '../config/app-config.service';
import { AuthStore } from './auth.store';

/**
 * Loader async de OIDC: mapea `config.json` (ya cargado por el APP_INITIALIZER
 * de config) a la config de `angular-auth-oidc-client`.
 * Authorization Code + PKCE + refresh token con silent renew.
 */
function stsConfigLoaderFactory(appConfig: AppConfigService): StsConfigLoader {
  // defer: el factory corre al crear el injector, ANTES del APP_INITIALIZER que
  // carga config.json. Leer config() acá reventaría el bootstrap; diferido, la
  // lectura ocurre recién cuando la lib se suscribe (post-inicialización).
  return new StsConfigHttpLoader(
    defer(() => of(appConfig.config().auth)).pipe(
      map((cfg) => ({
        authority: cfg.authority,
        clientId: cfg.clientId,
        redirectUrl: cfg.redirectUrl,
        postLogoutRedirectUri: cfg.postLogoutRedirectUri,
        scope: cfg.scope,
        responseType: 'code',
        useRefreshToken: true,
        silentRenew: true,
        renewTimeBeforeTokenExpiresInSeconds: 60,
        autoUserInfo: false,
        logLevel: LogLevel.Warn,
        // PKCE va implícito con responseType 'code' en la lib.
      })),
    ),
  );
}

/**
 * Provee el stack de autenticación:
 * - `angular-auth-oidc-client` configurado desde runtime config
 * - inicialización del AuthStore en el APP_INITIALIZER (procesa el callback OIDC)
 *
 * Debe ir DESPUÉS de `provideAppConfig()` en la lista de providers.
 *
 * NOTA C4 — Provisioning MANUAL (no hay SSO automatizado): OIDC es SOLO para el
 * LOGIN. El alta/baja de usuarios y choferes es MANUAL, a cargo del admin desde
 * la consola Admin (feature A4); NO existe integración SSO que aprovisione
 * cuentas de forma automática. Un chofer dado de baja pierde acceso INMEDIATO
 * en la app. Referencia: docs/respuestas-owner-negocio.md (C4).
 */
export function provideAuth(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideOidcAuth({
      loader: {
        provide: StsConfigLoader,
        useFactory: stsConfigLoaderFactory,
        deps: [AppConfigService],
      },
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: (store: AuthStore) => () => store.initialize(),
      deps: [AuthStore],
      multi: true,
    },
  ]);
}
